import { Navigate, BrowserRouter, Routes, Route } from 'react-router-dom';
import type { RouteObject } from 'react-router-dom';
import NotFound from '@/notFound';
import ErrorBoundaryLayout from '@/layout/ErrorBoundaryLayout';
import Login from '@/login';
import { MenuPath } from '@/facilities/model/user';
import CustomSpinLoading from '@/components/CustomSpinLoading';
import NotPermission from '@/pages/permission/403';
import menu, { isVisibleMenu } from '@/router/menu';
import { lazy, useEffect, Suspense, useCallback, useMemo } from 'react';
import { PageEnum, USER_MANAG, ISLOGIN, ADMIN } from '@/enums/pageEnum';
import { setMenus } from '@/store/modules/app';
import { useAppSelector, useAppDispatch } from '@/store';
import { message } from 'antd';

// 优化的 lazyLoad 函数，支持更好的代码分割和错误处理
const lazyLoad = (factory: () => Promise<any>, props?: any) => {
  const Module = lazy(() =>
    factory().catch((error) => {
      console.error('Failed to load module:', error);
      return {
        default: () => <NotPermission />,
      };
    }),
  );

  return (
    <Suspense fallback={<CustomSpinLoading />}>
      <Module {...props} />
    </Suspense>
  );
};

const loginRouter = [
  { path: PageEnum.BASE_LOGIN, element: <Login /> },
  {
    path: PageEnum.SET_PASSWORD,
    element: lazyLoad(async () => await import('@/login/setPassword')),
  },
  {
    path: PageEnum.RESET_PASSWORD,
    element: lazyLoad(async () => await import('@/login/setPassword')),
  },
  {
    path: PageEnum.CHOOSE_ORGAN,
    element: lazyLoad(async () => await import('@/login/chooseOrgan')),
  },
  {
    path: PageEnum.NO_PERMISS,
    element: lazyLoad(async () => await import('@/login/permissWarning')),
  },
  {
    path: PageEnum.JOIN_CREAT,
    element: lazyLoad(async () => await import('@/login/joinNow')),
  },
  {
    path: PageEnum.JOIN_SUCCESS,
    element: lazyLoad(async () => await import('@/login/inviteJoinSuccess')),
  },
  {
    path: PageEnum.LINK_EXPIRED,
    element: lazyLoad(async () => await import('@/login/linkExpired')),
  },
  {
    path: PageEnum.FORGET_PASS,
    element: lazyLoad(async () => await import('@/login/forgetPassword')),
  },
];

const modules = import.meta.glob(['../pages/*/index.tsx', '../pages/*/*/index.tsx'], {
  eager: true,
});
const components = Object.keys(modules).reduce<Record<string, any>>((prev, cur) => {
  const name = cur.match(/\.\.\/pages\/(.*)\/index\.tsx$/)![1];
  prev[name] = modules[cur];

  return prev;
}, {});

function MapMenusToRouter() {
  const dispatch = useAppDispatch();
  const roles = useAppSelector((state) => state.app.roles);
  const role = useAppSelector((state) => state.app.role);

  const lazyLoadComponent = useCallback((filePath: string | undefined, path?: string) => {
    if (!filePath || !components[filePath]) return null;
    return lazyLoad(async () => await components[filePath], { path });
  }, []);

  const getPathObject = useCallback(
    (item: MenuPath) => {
      const children: RouteObject[] = [];
      if (item.child) {
        item.child.map((li) => children.push(getPathObject(li)!));
      }
      return {
        id: item.routePath || item.path,
        path: item.routePath || item.path,
        children,
        element: lazyLoadComponent(item.filePath, item.path),
      };
    },
    [lazyLoadComponent],
  );

  useEffect(() => {
    try {
      const menus: MenuPath = { path: '/', routes: [] };
      menu.routes?.forEach(({ path, filePath, routes, child, name, defaultParams }) => {
        const isShowUserManga = name === USER_MANAG && role === ADMIN;
        if (name && roles.filter((item) => item.includes(name!)).length === 0) return;
        if (!isVisibleMenu(name)) return;
        const currentPath: any = {
          path,
          filePath,
          child,
          name,
          defaultParams,
        };
        if (routes) {
          currentPath.routes = [];
          if (isShowUserManga) {
            currentPath.routes = routes;
          } else {
            routes.forEach((item) => {
              if (roles.filter((key) => key.includes(item.name!)).length === 0) return;
              currentPath.routes.push(item);
            });
          }
        }
        menus.routes?.push(currentPath);
      });
      dispatch(setMenus(menus));
    } catch (error) {
      message.error(error?.toString() || 'Fail!', 2.5);
    }
  }, [getPathObject, roles, dispatch, lazyLoadComponent, role]);

  const renderRoutes = useCallback((routes: RouteObject[]) => {
    const lists = routes?.map((route, index) => {
      return (
        <Route key={index} path={route.path} element={route.element}>
          {route.children && renderRoutes(route.children)}
        </Route>
      );
    });
    return lists;
  }, []);

  const navRouter = useMemo(() => {
    const isLog = localStorage.getItem(ISLOGIN) === 'true';
    if (roles.length && isLog) {
      const redayRoutes: RouteObject[] = [];
      menu.routes?.forEach(({ path, filePath, routes, child }) => {
        const children: RouteObject[] = [];
        child?.map((item) => children.push(getPathObject(item)!));
        routes?.map((item) => {
          const croute = getPathObject(item);
          if (croute) {
            children.push(croute);
          }
        });
        redayRoutes.push({
          path,
          id: path,
          children,
          element: lazyLoadComponent(filePath, path),
        });
      });
      return (
        <Route path="/" element={<ErrorBoundaryLayout router={redayRoutes} />}>
          {renderRoutes(redayRoutes)}
          <Route path="/403" element={<NotPermission />}></Route>
        </Route>
      );
    }
    return <Route path="/" element={<Navigate to={'/login'} />} />;
  }, [renderRoutes, getPathObject, roles, lazyLoadComponent]);

  return (
    <BrowserRouter>
      <Routes>
        {navRouter}
        {loginRouter.map((item) => {
          return <Route key={item.path} path={item.path} element={item.element} />;
        })}
        <Route
          path="*"
          element={
            localStorage.getItem(ISLOGIN) === 'true' && !roles.length ? (
              <CustomSpinLoading />
            ) : (
              <NotFound />
            )
          }
        />
      </Routes>
    </BrowserRouter>
  );
}

export default MapMenusToRouter;
